import React, { lazy } from 'react'
import { Route, Redirect, Switch } from 'react-router-dom'
import { ConnectedRouter } from 'connected-react-router'
import { CSSTransition, SwitchTransition } from 'react-transition-group'
import { connect } from 'react-redux'
import Layout from 'layouts'
import ProtectedRoute from 'protectedRoutes'
import { userRoleEnums } from 'enums/userRole'

const ADMIN_ROLE = userRoleEnums?.ADMIN
const EMPLOYEE_ROLE = userRoleEnums?.EMPLOYEE
const USER_ROLE = userRoleEnums?.USER
const HR_ROLE = userRoleEnums?.HR
const TRAINER_ROLE = userRoleEnums?.TRAINER
const SENIOR_HR_MANAGER_ROLE = userRoleEnums?.SENIOR_HR_MANAGER
const WORKFLOW_TASK_ROLES = [ADMIN_ROLE, EMPLOYEE_ROLE, USER_ROLE, HR_ROLE, TRAINER_ROLE].filter(
  Boolean,
)
const PAYROLL_MANAGER_ROLE = userRoleEnums?.PAYROLL_MANAGER

// Who may open the reports area at all. Which reports they see inside it, and
// which rows each one returns, is decided by the backend registry and
// reportScopeService - not here.
const REPORT_VIEWER_ROLES = [
  ADMIN_ROLE,
  HR_ROLE,
  SENIOR_HR_MANAGER_ROLE,
  PAYROLL_MANAGER_ROLE,
  userRoleEnums?.FINANCE,
  userRoleEnums?.MANAGER,
  userRoleEnums?.ASSISTANT_MANAGER,
  userRoleEnums?.HOD,
].filter(Boolean)

const routes = [
  // VB:REPLACE-START:ROUTER-CONFIG
  {
    path: '/dashboard',
    Component: lazy(() => import('pages/dashboard/gamma')),
    exact: true,
    roles: [EMPLOYEE_ROLE, USER_ROLE, ADMIN_ROLE, HR_ROLE],
    modules: ['dashboard'],
  },
  {
    path: '/dashboard/alpha',
    Component: lazy(() => import('pages/dashboard/alpha')),
    exact: true,
    roles: [EMPLOYEE_ROLE, USER_ROLE, ADMIN_ROLE],
    modules: [],
  },
  {
    path: '/dashboard/beta',
    Component: lazy(() => import('pages/dashboard/beta')),
    exact: true,
    roles: [EMPLOYEE_ROLE, USER_ROLE, ADMIN_ROLE],
    modules: [],
  },
  {
    path: '/dashboard/gamma',
    Component: lazy(() => import('pages/dashboard/gamma')),
    exact: true,
    roles: [EMPLOYEE_ROLE, USER_ROLE, ADMIN_ROLE, HR_ROLE, TRAINER_ROLE],
    modules: ['dashboard'],
  },
  {
    path: '/dashboard/crypto',
    Component: lazy(() => import('pages/dashboard/crypto')),
    exact: true,
    roles: [EMPLOYEE_ROLE, USER_ROLE, ADMIN_ROLE],
    modules: [],
  },

  // configuration paths
  {
    path: '/config/term-and-condition',
    Component: lazy(() => import('pages/config/termAndCondition')),
    exact: true,
    roles: [ADMIN_ROLE],
    modules: ['terms_and_condition'],
  },
  {
    path: '/config/company-policy',
    Component: lazy(() => import('pages/config/companyPolicy')),
    exact: true,
    roles: [ADMIN_ROLE],
    modules: ['company_policy'],
  },
  {
    path: '/config/leave-policy',
    Component: lazy(() => import('pages/config/leavePolicy')),
    exact: true,
    roles: [ADMIN_ROLE, HR_ROLE],
    modules: ['leave_policy'],
  },
  {
    path: '/config/payroll-settings',
    Component: lazy(() => import('pages/config/payrollSettings')),
    exact: true,
    roles: [ADMIN_ROLE],
    modules: ['payroll_settings_master', 'payroll_settings'],
  },
  {
    path: '/config/salary-components',
    Component: lazy(() => import('pages/config/salaryComponents')),
    exact: true,
    roles: [ADMIN_ROLE],
    modules: ['salary_component_master'],
  },
  {
    path: '/config/salary-structures',
    Component: lazy(() => import('pages/config/salaryStructures')),
    exact: true,
    roles: [ADMIN_ROLE],
    modules: ['salary_structure_master'],
  },
  {
    path: '/config/payroll-compliance',
    Component: lazy(() => import('pages/config/compliance')),
    exact: true,
    roles: [ADMIN_ROLE],
    modules: ['payroll_compliance', 'payroll_settings'],
  },
  {
    path: '/config/doAndDont',
    Component: lazy(() => import('pages/DoAndDont')),
    exact: true,
    roles: [ADMIN_ROLE],
    modules: ["do_and_don't"],
  },
  {
    path: '/config/permissions',
    Component: lazy(() => import('pages/config/permissions')),
    exact: true,
    roles: [ADMIN_ROLE],
    modules: ['role_and_permissions'],
  },
  {
    path: '/config/hr-settings',
    Component: lazy(() => import('pages/config/hrSettings')),
    exact: true,
    roles: [ADMIN_ROLE, HR_ROLE],
    modules: ['hr_settings'],
  },
  {
    path: '/config/module-permission',
    Component: lazy(() => import('pages/config/modulePermission')),
    exact: true,
    roles: [ADMIN_ROLE],
    modules: ['module_permission'],
  },
  {
    path: '/config/audit-activity-logs',
    Component: lazy(() => import('pages/config/auditActivityLogs')),
    exact: true,
    roles: [ADMIN_ROLE],
    modules: ['audit_activity_logs'],
  },
  {
    path: '/config/roster-shift-master',
    Component: lazy(() => import('pages/config/rosterShiftMaster')),
    exact: true,
    roles: [ADMIN_ROLE],
    modules: ['roster_shift_master'],
  },
  {
    path: '/config/shift-team-mapping',
    Component: lazy(() => import('pages/config/shiftTeamMapping/index')),
    exact: true,
    roles: [ADMIN_ROLE, HR_ROLE],
    modules: ['shift_team_mapping', 'roster_shift_master'],
  },
  {
    path: '/config/global-type-category',
    Component: lazy(() => import('pages/config/globaltypecategory')),
    exact: true,
    roles: [ADMIN_ROLE],
    modules: ['global_type_category'],
  },
  {
    path: '/config/global-type',
    Component: lazy(() => import('pages/config/globaltype')),
    exact: true,
    roles: [ADMIN_ROLE],
    modules: ['global_type'],
  },
  // hierarchy
  {
    path: '/config/hierarchy',
    Component: lazy(() => import('pages/config/hierarchy')),
    exact: true,
    roles: [ADMIN_ROLE],
    modules: ['hierarchy'],
  },
  // location
  {
    path: '/config/location',
    Component: lazy(() => import('pages/location')),
    exact: true,
    roles: [ADMIN_ROLE],
    modules: ['location'],
  },
  // staff

  {
    path: '/staff',
    Component: lazy(() => import('pages/staff')),
    exact: true,
    roles: [ADMIN_ROLE, EMPLOYEE_ROLE, USER_ROLE],
    modules: ['staff'],
  },
  // employee management
  {
    path: '/emp/all-employee',
    Component: lazy(() => import('pages/emp')),
    exact: true,
    roles: [ADMIN_ROLE, HR_ROLE],
    modules: ['employees'],
  },
  {
    path: '/emp/all-agents',
    Component: lazy(() => import('pages/agent')),
    exact: true,
    roles: [ADMIN_ROLE, HR_ROLE, TRAINER_ROLE],
    modules: ['agents'],
  },
  // onBoarding
  {
    path: '/on-board',
    Component: lazy(() => import('pages/onboard')),
    exact: true,
    roles: [ADMIN_ROLE, HR_ROLE],
    modules: ['on_board', 'talent_onboarding'],
  },
  {
    path: '/on-board-ats',
    Component: lazy(() => import('pages/onboardats')),
    exact: true,
    roles: [ADMIN_ROLE, HR_ROLE],
    modules: ['on_board_ats'],
  },
  {
    path: '/ats/resumes/upload',
    Component: lazy(() => import('pages/ats/resumeUpload')),
    exact: true,
    roles: [ADMIN_ROLE, HR_ROLE],
    modules: ['resume_list'],
  },
  {
    path: '/ats/resume-requirements',
    Component: lazy(() => import('pages/ats/resumeRequirement')),
    exact: true,
    roles: [ADMIN_ROLE, HR_ROLE, EMPLOYEE_ROLE, USER_ROLE],
    modules: ['resume_requirements'],
  },
  {
    path: '/process-automation',
    Component: lazy(() => import('pages/ats/workflowEngine')),
    exact: true,
    roles: [ADMIN_ROLE],
    modules: ['process_automation'],
  },
  {
    path: '/process-automation/definitions',
    Component: lazy(() => import('pages/ats/workflowEngine')),
    exact: true,
    roles: [ADMIN_ROLE],
    modules: ['process_automation'],
  },
  {
    path: '/process-automation/templates',
    Component: lazy(() => import('pages/ats/workflowEngine')),
    exact: true,
    roles: [ADMIN_ROLE],
    modules: ['process_automation'],
  },
  {
    path: '/process-automation/builder',
    Component: lazy(() => import('pages/ats/workflowEngine')),
    exact: true,
    roles: [ADMIN_ROLE],
    modules: ['process_automation'],
  },
  {
    path: '/process-automation/instances',
    Component: lazy(() => import('pages/ats/workflowEngine')),
    exact: true,
    roles: [ADMIN_ROLE],
    modules: ['process_automation'],
  },
  {
    path: '/automation/tasks',
    Component: lazy(() => import('pages/ats/workflowEngine')),
    exact: true,
    roles: WORKFLOW_TASK_ROLES,
    modules: ['review_and_approval'],
  },
  {
    path: '/process-automation/tasks',
    Component: lazy(() => import('pages/ats/workflowEngine')),
    exact: true,
    // roles: WORKFLOW_TASK_ROLES,
    modules: ['review_and_approval'],
  },
  {
    path: '/process-automation/logs',
    Component: lazy(() => import('pages/ats/workflowEngine')),
    exact: true,
    roles: [ADMIN_ROLE],
    modules: ['process_automation'],
  },
  {
    path: '/ats/process-automation',
    Component: lazy(() => import('pages/ats/workflowEngine')),
    exact: true,
    roles: [ADMIN_ROLE],
    modules: ['process_automation'],
  },
  {
    path: '/ats/process-automation/definitions',
    Component: lazy(() => import('pages/ats/workflowEngine')),
    exact: true,
    roles: [ADMIN_ROLE],
    modules: ['process_automation'],
  },
  {
    path: '/ats/process-automation/templates',
    Component: lazy(() => import('pages/ats/workflowEngine')),
    exact: true,
    roles: [ADMIN_ROLE],
    modules: ['process_automation'],
  },
  {
    path: '/ats/process-automation/builder',
    Component: lazy(() => import('pages/ats/workflowEngine')),
    exact: true,
    roles: [ADMIN_ROLE],
    modules: ['process_automation'],
  },
  {
    path: '/ats/process-automation/instances',
    Component: lazy(() => import('pages/ats/workflowEngine')),
    exact: true,
    roles: [ADMIN_ROLE],
    modules: ['process_automation'],
  },
  {
    path: '/ats/process-automation/tasks',
    Component: lazy(() => import('pages/ats/workflowEngine')),
    exact: true,
    // roles: WORKFLOW_TASK_ROLES,
    modules: ['review_and_approval'],
  },
  {
    path: '/ats/process-automation/logs',
    Component: lazy(() => import('pages/ats/workflowEngine')),
    exact: true,
    roles: [ADMIN_ROLE],
    modules: ['process_automation'],
  },
  {
    path: '/ats/resumes/:id',
    Component: lazy(() => import('pages/ats/resumeDetails')),
    exact: true,
    roles: [ADMIN_ROLE, HR_ROLE, EMPLOYEE_ROLE, USER_ROLE],
    modules: ['resume_list'],
  },
  // Attendance
  {
    path: '/attendance',
    Component: lazy(() => import('pages/attendance')),
    exact: true,
    roles: [ADMIN_ROLE, HR_ROLE, EMPLOYEE_ROLE, USER_ROLE],
    modules: ['attendance'],
  },
  // down time
  {
    path: '/down-time',
    Component: lazy(() => import('pages/downTime')),
    exact: true,
    roles: [ADMIN_ROLE, EMPLOYEE_ROLE, USER_ROLE],
    modules: ['down_time'],
  },
  // roster upload
  {
    path: '/roster',
    Component: lazy(() => import('pages/roster')),
    exact: true,
    roles: [ADMIN_ROLE],
    modules: ['roster'],
  },
  {
    path: '/roster/auto-batches/:id',
    Component: lazy(() => import('pages/roster/autoBatchDetails')),
    exact: true,
    roles: [ADMIN_ROLE],
    modules: ['roster'],
  },
  {
    path: '/roster/rotation-rules',
    Component: lazy(() => import('pages/roster/rotationRules')),
    exact: true,
    roles: [ADMIN_ROLE],
    modules: ['roster_rotation_rules', 'roster'],
  },
  // exceptions
  {
    path: '/attendance-regularisation',
    Component: lazy(() => import('pages/exception')),
    exact: true,
    roles: [ADMIN_ROLE, EMPLOYEE_ROLE, USER_ROLE, HR_ROLE],
    modules: ['attendance_regularisation'],
  },
  // Leave management
  {
    path: '/calendar',
    Component: lazy(() => import('pages/leave/employeeLeave')),
    exact: true,
    roles: [HR_ROLE, ADMIN_ROLE, EMPLOYEE_ROLE, USER_ROLE],
    modules: ['calender'],
  },
  // Leave approval
  {
    path: '/leave-request',
    Component: lazy(() => import('pages/leave/leaveRequest')),
    exact: true,
    roles: [HR_ROLE, ADMIN_ROLE, EMPLOYEE_ROLE, USER_ROLE],
    modules: ['leave_request', 'leave_approval', 'calender'],
  },
  // Leave balance
  {
    path: '/leave-balance',
    Component: lazy(() => import('pages/leave/leaveBalance')),
    exact: true,
    roles: [HR_ROLE, ADMIN_ROLE, EMPLOYEE_ROLE, USER_ROLE],
    modules: ['profile'],
  },
  {
    path: '/comp-off',
    Component: lazy(() => import('pages/leave/compOff')),
    exact: true,
    roles: [HR_ROLE, ADMIN_ROLE, EMPLOYEE_ROLE, USER_ROLE],
    modules: ['comp_off'],
  },
  // RYG Status
  {
    path: '/RAGstatus',
    Component: lazy(() => import('pages/RYGStatus')),
    exact: true,
    roles: [HR_ROLE, ADMIN_ROLE, EMPLOYEE_ROLE, USER_ROLE],
    modules: ['ryg_status'],
  },
  // Happy to Help
  {
    path: '/happy-to-help',
    Component: lazy(() => import('pages/happyToHelp')),
    exact: true,
    modules: ['happy_to_help'],
    roles: [ADMIN_ROLE, EMPLOYEE_ROLE, USER_ROLE],
  },
  // Resignation
  {
    path: '/resignation',
    Component: lazy(() => import('pages/resignation')),
    exact: true,
    modules: ['resignation'],
    roles: [ADMIN_ROLE, EMPLOYEE_ROLE, USER_ROLE],
  },

  // Edit Profile
  {
    path: '/profile',
    Component: lazy(() => import('pages/editProfile')),
    exact: true,
    modules: ['profile'],
    roles: [ADMIN_ROLE, EMPLOYEE_ROLE, USER_ROLE],
  },
  // Account Settings (subscription plan) - admin only
  {
    path: '/account-settings',
    Component: lazy(() => import('pages/accountSettings')),
    exact: true,
    modules: ['profile'],
    roles: [ADMIN_ROLE],
  },
  // Salary slip for user
  {
    path: '/salary-slip',
    Component: lazy(() => import('pages/salarySlip/index')),
    exact: true,
    modules: ['salary_slip'],
    roles: [ADMIN_ROLE, EMPLOYEE_ROLE, USER_ROLE],
  },
  // Documents
  {
    path: '/documents',
    Component: lazy(() => import('pages/documents')),
    exact: true,
    modules: ['documents'],
    roles: [ADMIN_ROLE, HR_ROLE],
  },
  // encryption
  {
    path: '/documents/:employeeId',
    Component: lazy(() => import('pages/documents/documentListTable')),
    exact: true,
    modules: ['documents'],
    roles: [ADMIN_ROLE, HR_ROLE],
  },
  // payroll
  {
    path: '/salary',
    Component: lazy(() => import('pages/payroll')),
    exact: true,
    roles: [ADMIN_ROLE, HR_ROLE, PAYROLL_MANAGER_ROLE, EMPLOYEE_ROLE, USER_ROLE, ''],
    modules: ['salary'],
  },
  {
    path: '/payroll',
    Component: lazy(() => import('pages/payroll/payrollDashboard/index')),
    exact: true,
    roles: [ADMIN_ROLE, HR_ROLE, PAYROLL_MANAGER_ROLE, EMPLOYEE_ROLE, USER_ROLE],
    modules: ['payroll'],
  },
  {
    path: '/payroll-processing',
    Component: lazy(() => import('pages/payroll/payrollProcessing/index')),
    exact: true,
    roles: [ADMIN_ROLE, HR_ROLE, PAYROLL_MANAGER_ROLE, ''],
    modules: ['payroll_processing'],
  },
  {
    path: '/emp-self-service',
    Component: lazy(() => import('pages/payroll/employeeSelfService')),
    exact: true,
    roles: [EMPLOYEE_ROLE, USER_ROLE],
    modules: ['employee_self_service'],
  },
  {
    path: '/payroll-processing/payslips/:payslipId',
    Component: lazy(() => import('pages/payroll/payrollProcessing/payslip')),
    exact: true,
    roles: [ADMIN_ROLE, HR_ROLE, PAYROLL_MANAGER_ROLE, EMPLOYEE_ROLE, USER_ROLE, ''],
    modules: ['payroll_processing', 'payroll_dashboard', 'employee_self_service'],
  },
  {
    path: '/payroll-processing/review',
    Component: lazy(() => import('pages/payroll/payrollProcessing/details')),
    exact: true,
    roles: [ADMIN_ROLE, HR_ROLE, PAYROLL_MANAGER_ROLE, ''],
    modules: ['payroll_processing'],
  },
  {
    path: '/fnf-settlement',
    Component: lazy(() => import('pages/fnf')),
    exact: true,
    roles: [ADMIN_ROLE, HR_ROLE, PAYROLL_MANAGER_ROLE, ''],
    modules: ['fnf_settlement'],
  },
  {
    path: '/bonuses-incentives',
    Component: lazy(() => import('pages/payroll/bonusesIncentives/index')),
    exact: true,
    roles: [ADMIN_ROLE, HR_ROLE, PAYROLL_MANAGER_ROLE, EMPLOYEE_ROLE, USER_ROLE, ''],
    modules: ['bonuses_and_incentives'],
  },
  {
    path: '/bonuses-incentives/new',
    Component: lazy(() => import('pages/payroll/bonusesIncentives/details')),
    exact: true,
    roles: [ADMIN_ROLE, HR_ROLE, PAYROLL_MANAGER_ROLE, ''],
    modules: ['bonuses_and_incentives'],
  },
  {
    path: '/bonuses-incentives/:entryId',
    Component: lazy(() => import('pages/payroll/bonusesIncentives/details')),
    exact: true,
    roles: [ADMIN_ROLE, HR_ROLE, PAYROLL_MANAGER_ROLE, ''],
    modules: ['bonuses_and_incentives'],
  },
  // encryption
  {
    path: '/payroll/:userId',
    Component: lazy(() => import('pages/payroll/viewPayroll')),
    exact: true,
    modules: ['payroll', 'salary'],
    roles: [EMPLOYEE_ROLE, USER_ROLE, ADMIN_ROLE, HR_ROLE, PAYROLL_MANAGER_ROLE],
  },
  {
    path: '/payroll-dashboard',
    Component: lazy(() => import('pages/payroll/payrollDashboard/index')),
    exact: true,
    modules: ['payroll_dashboard'],
    roles: [EMPLOYEE_ROLE, USER_ROLE, ADMIN_ROLE, HR_ROLE, PAYROLL_MANAGER_ROLE],
  },
  // Auth
  {
    path: '/auth/login',
    Component: lazy(() => import('pages/auth/login')),
    exact: true,
    modules: ['logIn'],
    roles: [EMPLOYEE_ROLE, USER_ROLE, ADMIN_ROLE],
  },
  {
    path: '/auth/forgot-password',
    Component: lazy(() => import('pages/auth/forgot-password')),
    exact: true,
    modules: [],
    roles: [EMPLOYEE_ROLE, USER_ROLE, ADMIN_ROLE],
  },
  {
    path: '/auth/reset-password',
    Component: lazy(() => import('pages/auth/reset-password')),
    exact: true,
    modules: [],
    roles: [EMPLOYEE_ROLE, USER_ROLE, ADMIN_ROLE],
  },
  {
    path: '/auth/temporary-password-reset',
    Component: lazy(() => import('pages/auth/temporary-password-reset')),
    exact: true,
    modules: [],
    roles: [EMPLOYEE_ROLE, USER_ROLE, ADMIN_ROLE],
  },
  {
    path: '/auth/register',
    Component: lazy(() => import('pages/auth/register')),
    exact: true,
    modules: [],
    roles: [EMPLOYEE_ROLE, USER_ROLE, ADMIN_ROLE],
  },
  {
    path: '/auth/lockscreen',
    Component: lazy(() => import('pages/auth/lockscreen')),
    exact: true,
    modules: ['lockscreen'],
    roles: [ADMIN_ROLE, EMPLOYEE_ROLE, USER_ROLE],
  },
  {
    path: '/under-construction',
    Component: lazy(() => import('pages/underConstruction')),
    exact: true,
    modules: ['under_construction'],
    roles: [ADMIN_ROLE],
  },
  {
    path: '/teams',
    Component: lazy(() => import('pages/teams')),
    exact: true,
    modules: ['teams'],
    roles: [ADMIN_ROLE],
  },
  {
    path: '/acknowledgement',
    Component: lazy(() => import('pages/acknowledgement')),
    exact: true,
    modules: ['acknowledgement'],
    roles: [EMPLOYEE_ROLE, USER_ROLE, ADMIN_ROLE, HR_ROLE],
  },
  // encryption
  {
    path: '/acknowledgement/:docId',
    Component: lazy(() => import('pages/acknowledgement/ViewDoc')),
    exact: true,
    modules: ['acknowledgement'],
    roles: [EMPLOYEE_ROLE, USER_ROLE, ADMIN_ROLE, HR_ROLE],
  },
  {
    path: '/auth/404',
    Component: lazy(() => import('pages/auth/404')),
    exact: true,
    modules: ['404'],
    roles: [EMPLOYEE_ROLE, USER_ROLE, ADMIN_ROLE],
  },
  {
    path: '/auth/500',
    Component: lazy(() => import('pages/auth/500')),
    exact: true,
    modules: ['500'],
    roles: [EMPLOYEE_ROLE, USER_ROLE, ADMIN_ROLE],
  },
  {
    path: '/auth/candidate-form',
    Component: lazy(() => import('pages/onboard/addCandidateForm/addForm')),
    exact: true,
    modules: [],
    roles: [EMPLOYEE_ROLE, USER_ROLE, ADMIN_ROLE],
  },
  {
    path: '/auth/candidate-bank-details-form',
    Component: lazy(() => import('pages/onboard/addCandidateBankForm/addBankDetailsForm')),
    exact: true,
    modules: [],
    roles: [EMPLOYEE_ROLE, USER_ROLE, ADMIN_ROLE],
  },
  {
    path: '/attendance-report',
    Component: lazy(() => import('pages/report/attendnaceReport')),
    exact: true,
    modules: ['attendance_report'],
    roles: [EMPLOYEE_ROLE, USER_ROLE, ADMIN_ROLE],
  },
  // encryption
  {
    path: '/attendance-report/:userId',
    Component: lazy(() => import('pages/report/attendnaceReport/report')),
    exact: true,
    modules: ['attendance_report'],
    roles: [EMPLOYEE_ROLE, USER_ROLE, ADMIN_ROLE],
  },
  {
    path: '/leave-report',
    Component: lazy(() => import('pages/report/leaveReport')),
    exact: true,
    modules: ['leave_report'],
    roles: [EMPLOYEE_ROLE, USER_ROLE, ADMIN_ROLE],
  },
  // Reporting module. The launcher and the generic viewer are the only two
  // routes: the report itself is resolved from :reportId against the backend
  // registry, which also applies the role gate and row-level scoping.
  {
    path: '/reports',
    Component: lazy(() => import('pages/reports')),
    exact: true,
    modules: ['reports_center'],
    roles: REPORT_VIEWER_ROLES,
  },
  {
    path: '/reports/:reportId',
    Component: lazy(() => import('pages/reports/ReportViewer')),
    exact: true,
    modules: ['reports_center'],
    roles: REPORT_VIEWER_ROLES,
  },
  // encryption
  {
    path: '/leave-report/:userId',
    Component: lazy(() => import('pages/report/leaveReport/report')),
    exact: true,
    modules: ['leave_report'],
    roles: [EMPLOYEE_ROLE, USER_ROLE, ADMIN_ROLE],
  },
  {
    path: '/expenses',
    Component: lazy(() => import('pages/expenses/index')),
    exact: true,
    modules: ['expenses'],
    roles: [HR_ROLE, ADMIN_ROLE],
  },
  {
    path: '/reimbursement',
    Component: lazy(() => import('pages/reimbursement/index')),
    exact: true,
    modules: ['reimbursement'],
    roles: [EMPLOYEE_ROLE, USER_ROLE, HR_ROLE, ADMIN_ROLE, PAYROLL_MANAGER_ROLE],
  },
  {
    path: '/batch',
    Component: lazy(() => import('pages/batch/index')),
    exact: true,
    modules: ['batch'],
    roles: [EMPLOYEE_ROLE, HR_ROLE, ADMIN_ROLE],
  },
  {
    path: '/training-and-assessment',
    Component: lazy(() => import('pages/trainingAndAssessment/index')),
    exact: true,
    modules: ['training_and_assessment'],
    roles: [HR_ROLE, ADMIN_ROLE],
  },
  {
    path: '/ticket-management',
    Component: lazy(() => import('pages/ticketingSystem')),
    exact: true,
    modules: ['ticket_management'],
    roles: [ADMIN_ROLE, EMPLOYEE_ROLE, USER_ROLE, 'trainee', HR_ROLE],
  },
  {
    path: '/ats-management',
    Component: lazy(() => import('pages/ats/resumeList/index')),
    exact: true,
    modules: ['resume_list'],
    roles: [ADMIN_ROLE, HR_ROLE, EMPLOYEE_ROLE, USER_ROLE],
  },
  {
    path: '/ticket-management/view-tickets/:ticket_id',
    Component: lazy(() => import('pages/ticketingSystem/viewTickets')),
    exact: true,
    modules: ['ticket_management'],
    roles: [ADMIN_ROLE],
  },

  {
    path: '/punching-report',
    Component: lazy(() => import('pages/punchingLogs/punchingHistory')),
    exact: true,
    modules: ['punching_report'],
    roles: [ADMIN_ROLE, HR_ROLE, SENIOR_HR_MANAGER_ROLE, EMPLOYEE_ROLE, USER_ROLE],
  },
  {
    path: '/config/custom-email-template',
    Component: lazy(() => import('pages/customEmailTemplate')),
    exact: true,
    modules: ['custom_template'],
    roles: [EMPLOYEE_ROLE, USER_ROLE],
  },
  {
    path: '/config/company-profile',
    Component: lazy(() => import('pages/companyProfile')),
    exact: true,
    roles: [ADMIN_ROLE],
    modules: ['profile'],
  },
  {
    path: '/weekly-off-rules',
    Component: lazy(() => import('pages/weeklyOffRule')),
    exact: true,
    roles: [ADMIN_ROLE],
    modules: ['weekly_off_rules'],
  },
  {
    path: '/weekly-off-rule/add',
    Component: lazy(() => import('pages/weeklyOffRule/addweeklyOff')),
    exact: true,
    roles: [ADMIN_ROLE],
    modules: ['weekly_off_rules'],
  },
  {
    path: '/holiday-management',
    Component: lazy(() => import('pages/holiday-management')),
    exact: true,
    roles: [ADMIN_ROLE, HR_ROLE],
    modules: ['holiday'],
  },

  // ------------------------------------------------------------------- CRM
  // Each `modules` key must exist as a plan feature in Admin_BE or the route is
  // invisible — that is the subscription gate working, not a bug. The keys here
  // match constants/moduleKeySeedData.js exactly.
  {
    path: '/crm/dashboard',
    Component: lazy(() => import('pages/crm/Dashboard')),
    exact: true,
    roles: [ADMIN_ROLE],
    modules: ['crm_dashboard'],
  },
  {
    path: '/crm/leads',
    Component: lazy(() => import('pages/crm/Leads/LeadList')),
    exact: true,
    roles: [ADMIN_ROLE],
    modules: ['crm_leads'],
  },
  {
    // ORDER MATTERS: these render through a <Switch>, so the literal 'create' path
    // MUST come before '/crm/leads/:leadId' or the detail route captures it and
    // tries to load a lead whose id is the string "create".
    path: '/crm/leads/create',
    Component: lazy(() => import('pages/crm/Leads/LeadForm')),
    exact: true,
    roles: [ADMIN_ROLE],
    modules: ['crm_leads'],
  },
  {
    path: '/crm/leads/:leadId/edit',
    Component: lazy(() => import('pages/crm/Leads/LeadForm')),
    exact: true,
    roles: [ADMIN_ROLE],
    modules: ['crm_leads'],
  },
  {
    path: '/crm/leads/:leadId',
    Component: lazy(() => import('pages/crm/Leads/LeadDetail')),
    exact: true,
    roles: [ADMIN_ROLE],
    modules: ['crm_leads'],
  },
  {
    path: '/crm/opportunities',
    Component: lazy(() => import('pages/crm/Opportunities/Pipeline')),
    exact: true,
    roles: [ADMIN_ROLE],
    modules: ['crm_opportunities'],
  },
  {
    path: '/crm/opportunities/create',
    Component: lazy(() => import('pages/crm/Opportunities/OpportunityForm')),
    exact: true,
    roles: [ADMIN_ROLE],
    modules: ['crm_opportunities'],
  },
  {
    path: '/crm/opportunities/:opportunityId/edit',
    Component: lazy(() => import('pages/crm/Opportunities/OpportunityForm')),
    exact: true,
    roles: [ADMIN_ROLE],
    modules: ['crm_opportunities'],
  },
  {
    path: '/crm/opportunities/:opportunityId',
    Component: lazy(() => import('pages/crm/Opportunities/OpportunityDetail')),
    exact: true,
    roles: [ADMIN_ROLE],
    modules: ['crm_opportunities'],
  },
  {
    path: '/crm/companies',
    Component: lazy(() => import('pages/crm/Companies/CompanyList')),
    exact: true,
    roles: [ADMIN_ROLE],
    modules: ['crm_companies'],
  },
  {
    // Literal before parameter — see the note on /crm/leads/create above.
    path: '/crm/companies/create',
    Component: lazy(() => import('pages/crm/Companies/CompanyForm')),
    exact: true,
    roles: [ADMIN_ROLE],
    modules: ['crm_companies'],
  },
  {
    path: '/crm/companies/:companyId/edit',
    Component: lazy(() => import('pages/crm/Companies/CompanyForm')),
    exact: true,
    roles: [ADMIN_ROLE],
    modules: ['crm_companies'],
  },
  {
    path: '/crm/companies/:companyId',
    Component: lazy(() => import('pages/crm/Companies/CompanyDetail')),
    exact: true,
    roles: [ADMIN_ROLE],
    modules: ['crm_companies'],
  },
  {
    path: '/crm/contacts',
    Component: lazy(() => import('pages/crm/Contacts/ContactList')),
    exact: true,
    roles: [ADMIN_ROLE],
    modules: ['crm_contacts'],
  },
  {
    path: '/crm/contacts/create',
    Component: lazy(() => import('pages/crm/Contacts/ContactForm')),
    exact: true,
    roles: [ADMIN_ROLE],
    modules: ['crm_contacts'],
  },
  {
    path: '/crm/contacts/:contactId/edit',
    Component: lazy(() => import('pages/crm/Contacts/ContactForm')),
    exact: true,
    roles: [ADMIN_ROLE],
    modules: ['crm_contacts'],
  },
  {
    // The follow-up buckets on the dashboard link here with ?bucket=…; the query
    // string is not part of the path, so one route serves all four.
    path: '/crm/activities',
    Component: lazy(() => import('pages/crm/Activities')),
    exact: true,
    roles: [ADMIN_ROLE],
    modules: ['crm_activities'],
  },
  {
    path: '/crm/calendar',
    Component: lazy(() => import('pages/crm/Calendar')),
    exact: true,
    roles: [ADMIN_ROLE],
    modules: ['crm_calendar'],
  },
  {
    path: '/crm/reports',
    Component: lazy(() => import('pages/crm/Reports')),
    exact: true,
    roles: [ADMIN_ROLE],
    modules: ['crm_reports'],
  },
  {
    // Reachable by any CRM user; the screen itself is read-only for non-admins and
    // the server refuses their writes regardless.
    path: '/crm/settings',
    Component: lazy(() => import('pages/crm/Settings')),
    exact: true,
    roles: [ADMIN_ROLE],
    modules: ['crm_settings'],
  },
]

const mapStateToProps = ({ settings }) => ({
  routerAnimation: settings.routerAnimation,
})

const Router = ({ history, routerAnimation }) => {
  return (
    <ConnectedRouter history={history}>
      <Layout>
        <Route
          render={(state) => {
            const { location } = state
            return (
              <SwitchTransition>
                <CSSTransition
                  key={location.pathname}
                  appear
                  classNames={routerAnimation}
                  timeout={routerAnimation === 'none' ? 0 : 300}
                >
                  <Switch location={location}>
                    {/* VB:REPLACE-NEXT-LINE:ROUTER-REDIRECT */}

                    <Route exact path="/" render={() => <Redirect to="/dashboard" />} />
                    {routes.map(({ path, Component, exact, roles, modules, permission }) => (
                      <ProtectedRoute
                        key={path}
                        path={path}
                        exact={exact}
                        Component={Component}
                        roles={roles}
                        modules={modules}
                        routerAnimation={routerAnimation}
                        permission={permission}
                      />
                    ))}
                    <Redirect to="/auth/404" />
                  </Switch>
                </CSSTransition>
              </SwitchTransition>
            )
          }}
        />
      </Layout>
    </ConnectedRouter>
  )
}

export default connect(mapStateToProps)(Router)
